import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Tabs, Tab, Card, Button, Drawer, IconButton,
  TextField, Select, MenuItem, FormControlLabel, Checkbox, Grid, Paper, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Switch, Tooltip as MuiTooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import ShortTextIcon from '@mui/icons-material/ShortText';
import NotesIcon from '@mui/icons-material/Notes';
import DialpadIcon from '@mui/icons-material/Dialpad';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import FunctionsIcon from '@mui/icons-material/Functions';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SaveIcon from '@mui/icons-material/Save';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import axios from 'axios';
import { toast } from 'react-toastify';
import API_ENDPOINTS from '../../../config/apiEndpoints';
import { getSession } from '../../../getSession';
import Alert from '../../../common/Alerts/Alert';

const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');

const FIELD_TYPES = [
  { label: 'Text Field', type: 'v', icon: <ShortTextIcon sx={{ color: '#9c27b0' }} /> },
  { label: 'MultiLine Field', type: 'm', icon: <CheckBoxIcon sx={{ color: '#3f51b5' }} /> },
  { label: 'Numeric Field', type: 'f', icon: <SmartphoneIcon sx={{ color: '#2196f3' }} /> },
  { label: 'Date Field', type: 'd', icon: <CalendarMonthIcon sx={{ color: '#00bcd4' }} /> },
  { label: 'Radio Field', type: 'r', icon: <RadioButtonCheckedIcon sx={{ color: '#607d8b' }} /> },
  { label: 'Checkbox Field', type: 'c', icon: <CheckBoxIcon sx={{ color: '#4caf50' }} /> },
  { label: 'Dropdown Field', type: 's', icon: <ArrowDropDownCircleIcon sx={{ color: '#ff9800' }} /> },
  { label: 'Multiple Select Field', type: 'l', icon: <ArrowDropDownCircleIcon sx={{ color: '#ff5722' }} /> },
  { label: 'Formula', type: 'e', icon: <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem', color: '#e91e63' }}>f<sub>x</sub></Typography> },
];

const EnquiryCustomField = ({ onClose }) => {
  const [tabValue, setTabValue] = useState(0);
  const [fieldsList, setFieldsList] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form State
  const [fieldId, setFieldId] = useState(0);
  const [fieldName, setFieldName] = useState('');
  const [dataType, setDataType] = useState('v');
  const [isMandatory, setIsMandatory] = useState(false);
  const [isQuickAdd, setIsQuickAdd] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [defaultValue, setDefaultValue] = useState('');
  const [minVal, setMinVal] = useState('0');
  const [maxVal, setMaxVal] = useState('0');
  const [choices, setChoices] = useState('');
  const [choiceList, setChoiceList] = useState([{ id: Date.now(), value: '' }]);
  const [tooltip, setTooltip] = useState('');
  const [formula, setFormula] = useState('');
  const [isCurrentDateDefault, setIsCurrentDateDefault] = useState(false);
  const [selectedFieldTypeLabel, setSelectedFieldTypeLabel] = useState('');

  const [alertConfig, setAlertConfig] = useState({
    show: false,
    type: 'success',
    title: 'Success',
    message: '',
    onConfirm: null
  });

  // Dependency States
  const [drawerTabValue, setDrawerTabValue] = useState(0);
  const [parentFields, setParentFields] = useState([]);
  const [childFields, setChildFields] = useState([]);
  const [parentFieldId, setParentFieldId] = useState('');
  const [childFieldId, setChildFieldId] = useState('');
  const [formulaParams, setFormulaParams] = useState([]);
  const [dependencyData, setDependencyData] = useState([]);
  const [parentChoices, setParentChoices] = useState([]);
  const [mappedValues, setMappedValues] = useState({}); // { parentChoiceValue: [childChoiceValues] }
  const [activeDependencyMode, setActiveDependencyMode] = useState('Parent'); // 'Parent' or 'Child'
  const [isDepModalOpen, setIsDepModalOpen] = useState(false);
  const [depTabValue, setDepTabValue] = useState(0);
  const [depChildFields, setDepChildFields] = useState([]);
  const [depChildValues, setDepChildValues] = useState([]);
  const [selectedDepChildId, setSelectedDepChildId] = useState('');
  const [selectedDepParentValue, setSelectedDepParentValue] = useState('');
  const [selectedDepChildValues, setSelectedDepChildValues] = useState([]);
  const [addedMappings, setAddedMappings] = useState([]); // List of { id, text, type }
  
  const [formulaList, setFormulaList] = useState([]);
  const [isParamModalOpen, setIsParamModalOpen] = useState(false);
  const [currentFormulaParams, setCurrentFormulaParams] = useState([]);
  const [paramMappings, setParamMappings] = useState([]); // Array of { ParamId, ParamName, ParamType, ParamValue }
  const [selectedFormulaId, setSelectedFormulaId] = useState('');
  const [allCrmFields, setAllCrmFields] = useState([]);

  // Reserved Keywords from app.aspx
  const RESERVED_KEYWORDS = [
    "sno", "add", "external", "procedure", "all", "fetch", "public", "alter", "file", "raiserror", "and", "fillfactor", "read", "any", "for", "readtext", "as", "foreign", "reconfigure", "asc", "freetext", "references", "authorization", "freetexttable", "replication", "backup", "from", "restore", "begin", "full", "restrict", "between", "function", "return", "break", "goto", "revert", "browse", "grant", "revoke", "bulk", "group", "right", "by", "having", "rollback", "cascade", "holdlock", "rowcount", "case", "identity", "rowguidcol", "check", "identity_insert", "rule", "checkpoint", "identitycol", "save", "close", "if", "schema", "clustered", "in", "securityaudit", "coalesce", "index", "select", "collate", "inner", "semantickeyphrasetable", "column", "insert", "semanticsimilaritydetailstable", "commit", "intersect", "semanticsimilaritytable", "compute", "into", "session_user", "constraint", "is", "set", "contains", "join", "setuser", "containstable", "key", "shutdown", "continue", "kill", "some", "convert", "left", "statistics", "create", "like", "system_user", "cross", "lineno", "table", "current", "load", "tablesample", "current_date", "merge", "textsize", "current_time", "national", "then", "current_timestamp", "nocheck", "to", "current_user", "nonclustered", "top", "cursor", "not", "tran", "database", "null", "transaction", "dbcc", "nullif", "trigger", "deallocate", "of", "truncate", "declare", "off", "try_convert", "default", "offsets", "tsequal", "delete", "on", "union", "deny", "open", "unique", "desc", "opendatasource", "unpivot", "distinct", "openquery", "update", "distinct", "openrowset", "updatetext", "distributed", "openxml", "use", "double", "option", "user", "drop", "or", "values", "dump", "order", "varying", "else", "outer", "view", "end", "over", "waitfor", "errlvl", "percent", "when", "escape", "pivot", "where", "except", "plan", "while", "exec", "precision", "with", "execute", "primary", "withingroup", "exists", "print", "writetext", "exit", "proc", "amountpaid", "assignedto", "campaignname", "city", "companyname", "country", "countrycode", "countrycode1", "countrycode2", "createdby", "createddate", "createdon", "emailid", "emailid1", "emailid2", "enquiryid", "enquirysource", "followupdate", "followupstatus", "id", "img_ext", "initialremarks", "isopen", "lastfollowupcreatedby", "lastfollowupcreatedon", "latitude", "leadimage", "leadno", "longitude", "mediumname", "mobileno", "mobileno1", "mobileno2", "modifiedby", "modifiedon", "nextstatusdate", "officeaddress", "parentid", "personname", "pincode", "products", "profileimgfilename", "remarks", "residentialaddress", "sourcename", "state", "temp_id", "whatsappnocolname", "whereaddress", "type", "isscoreeligible", "owner", "stages", "reminderdate", "stagesid", "remark"
  ];

  const fetchFormulaList = async () => {
    try {
      const payload = {
        Token: TokenId,
        Details: ""
      };
      const response = await axios.post(`${NEWV3_BASE_URL}/UserCRM/GetFormulaList`, payload);
      if (response.data && response.data.Status === 1) {
        setFormulaList(response.data.Details || []);
      }
    } catch (error) {
      console.error("Error fetching formula list", error);
    }
  };

  const fetchAllCrmFields = async () => {
    try {
      const payload = {
        Token: TokenId,
        Details: { UserId: 0 }
      };
      const response = await axios.post(`${NEWV3_BASE_URL}/UserCRM/GetColumnsForFxByUid`, payload);
      if (response.data && response.data.Status === 1) {
        setAllCrmFields(response.data.Details || []);
      }
    } catch (error) {
      console.error("Error fetching CRM fields", error);
    }
  };

  // Fetch from session
  const { TokenId, parentId } = getSession();

  const fetchFields = async (status) => {
    try {
      const session = getSession();
      const resolvedParentId = session.parentId || localStorage.getItem('ParentID') || localStorage.getItem('userId') || '0';
      const payload = {
        Token: "-2295521862261168",
        Details: {
          ParentId: resolvedParentId,
          Status: status // "Active" or "Inactive"
        }
      };

      const response = await axios.post(`${NEWV3_BASE_URL}${API_ENDPOINTS.ENQUIRIES.GET_CUSTOM_FIELDS}`, payload);

      if (response.data && response.data.Status === 1 && response.data.Details) {
        const rawFields = response.data.Details;
        // Filter fields based on requested status to ensure data integrity in the UI
        const filtered = rawFields.filter(f => {
          const isActiveField = f.isActive === true || f.IsActive === 'True' || String(f.IsActive).toLowerCase() === 'true';
          return status === 'Active' ? isActiveField : !isActiveField;
        });
        setFieldsList(filtered);
      } else {
        setFieldsList([]);
      }
    } catch (error) {
      console.error("Error fetching custom fields", error);
      toast.error("Failed to fetch custom fields.");
    }
  };

  const fetchParentFields = async () => {
    try {
      const session = getSession();
      const resolvedParentId = session.parentId || localStorage.getItem('ParentID') || localStorage.getItem('userId') || '0';
      const payload = {
        Token: TokenId,
        Details: {
          mode: "Parent",
          Id: "",
          ParentId: resolvedParentId
        }
      };

      const response = await axios.post(`${NEWV3_BASE_URL}${API_ENDPOINTS.ENQUIRIES.BIND_PARENT_CHILD}`, payload);
      if (response.data && response.data.Status === 1) {
        const details = response.data.Details || [];
        const mapped = details.map(f => ({
          Id: f.Id || f.id || f.field_ID || f.fieldID || 0,
          Text: f.Text || f.text || f.fieldname || f.choices || ''
        }));
        setParentFields(mapped);
      }
    } catch (error) {
      console.error("Error fetching parent fields", error);
    }
  };

  const fetchChildFields = async (pId) => {
    try {
      const session = getSession();
      const resolvedParentId = session.parentId || localStorage.getItem('ParentID') || localStorage.getItem('userId') || '0';
      const payload = {
        Token: TokenId,
        Details: {
          mode: "Child",
          Id: "",
          ParentId: resolvedParentId
        }
      };

      const response = await axios.post(`${NEWV3_BASE_URL}${API_ENDPOINTS.ENQUIRIES.BIND_PARENT_CHILD}`, payload);
      if (response.data && response.data.Status === 1) {
        const details = response.data.Details || [];
        const mapped = details.map(f => ({
          Id: f.Id || f.id || f.field_ID || f.fieldID || 0,
          Text: f.Text || f.text || f.fieldname || f.choices || ''
        }));
        setChildFields(mapped);
      }
    } catch (error) {
      console.error("Error fetching child fields", error);
    }
  };

  const fetchFormulaParamsByFormulaId = async (fId) => {
    try {
      const session = getSession();
      const resolvedParentId = session.parentId || localStorage.getItem('ParentID') || localStorage.getItem('userId') || '0';
      const payload = {
        Token: TokenId,
        Details: {
          Id: fId,
          ParentId: resolvedParentId
        }
      };

      const response = await axios.post(`${NEWV3_BASE_URL}/UserCRM/GetFormulaParamDetail`, payload);
      if (response.data && response.data.Status === 1) {
        const params = response.data.Details || [];
        setCurrentFormulaParams(params);
        // Initialize mappings
        const initialMappings = params.map(p => ({
          ParamId: String(p.Id || p.id),
          ParamName: p.Text || p.text || p.ParamName || '',
          ParamType: 'Field',
          ParamValue: ''
        }));
        setParamMappings(initialMappings);
        setIsParamModalOpen(true);
      }
    } catch (error) {
      console.error("Error fetching formula params", error);
    }
  };

  const fetchParentChoices = async (pId, modeOverride) => {
    try {
      const session = getSession();
      const resolvedParentId = session.parentId || localStorage.getItem('ParentID') || localStorage.getItem('userId') || '0';
      const modeToUse = modeOverride || activeDependencyMode;
      const payload = {
        Token: TokenId,
        Details: {
          mode: modeToUse === 'Parent' ? "ChildMapValue" : "ParentMapValue",
          FieldValue: String(pId),
          ParentId: resolvedParentId
        }
      };

      const response = await axios.post(`${NEWV3_BASE_URL}/UserCRM/BindChildtochildParenttoParent`, payload);
      if (response.data && response.data.Status === 1) {
        const details = response.data.Details || [];
        const mapped = details.map(f => ({
          Id: f.Id || f.id || f.field_ID || f.fieldID || 0,
          Text: f.Text || f.text || f.fieldname || f.choices || ''
        }));
        setParentChoices(mapped);
      }
    } catch (error) {
      console.error("Error fetching parent choices", error);
    }
  };

  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    fetchFields(tabValue === 0 ? 'Active' : 'Inactive');
  }, [tabValue]);

  const fetchDepChildFields = async (mode = "Child") => {
    try {
      const session = getSession();
      const resolvedParentId = session.parentId || localStorage.getItem('ParentID') || localStorage.getItem('userId') || '0';
      const payload = {
        Token: TokenId,
        Details: { mode: mode, ParentId: resolvedParentId }
      };
      const response = await axios.post(`${NEWV3_BASE_URL}/UserCRM/BindParentChildDetail`, payload);
      if (response.data && response.data.Status === 1) {
        setDepChildFields(response.data.Details || []);
      }
    } catch (error) {
      console.error("Error fetching dep child fields", error);
    }
  };

  useEffect(() => {
    if (activeDependencyMode === 'Child' && isDepModalOpen) {
      setParentChoices(choiceList.map(c => ({ Id: c.id, Text: c.value })));
    }
  }, [choiceList, activeDependencyMode, isDepModalOpen]);

  const fetchDepChildValues = async (childId, modeOverride) => {
    try {
      const session = getSession();
      const resolvedParentId = session.parentId || localStorage.getItem('ParentID') || localStorage.getItem('userId') || '0';
      const modeToUse = modeOverride || activeDependencyMode;
      const payload = {
        Token: TokenId,
        Details: { 
          mode: modeToUse === 'Parent' ? "ChildMapValue" : "ParentMapValue", 
          FieldValue: String(childId), 
          ParentId: resolvedParentId 
        }
      };
      const response = await axios.post(`${NEWV3_BASE_URL}/UserCRM/BindChildtochildParenttoParent`, payload);
      if (response.data && response.data.Status === 1) {
        setDepChildValues(response.data.Details || []);
      }
    } catch (error) {
      console.error("Error fetching dep child values", error);
    }
  };

  const fetchMappingChoices = async (fieldId) => {
    try {
      const session = getSession();
      const resolvedParentId = session.parentId || localStorage.getItem('ParentID') || localStorage.getItem('userId') || '0';
      const payload = {
        Token: TokenId,
        Details: { 
          mode: activeDependencyMode === 'Parent' ? "ChildMapValue" : "ParentMapValue",
          FieldValue: String(fieldId),
          ParentId: resolvedParentId
        }
      };
      // Use the specific endpoint requested for mapping choices
      const response = await axios.post(`${NEWV3_BASE_URL}/UserCRM/BindChildtochildParenttoParent`, payload);
      if (response.data && response.data.Status === 1) {
        setParentChoices(response.data.Details || []);
      }
    } catch (error) {
      console.error("Error fetching mapping choices", error);
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      fetchFormulaList();
      fetchAllCrmFields();
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (activeDependencyMode === 'Child') {
      // Current field is Parent, use its own choices for the mapping table
      setParentChoices(choiceList.map(c => ({ Id: c.id, Text: c.value })));
    }
  }, [activeDependencyMode, choiceList]);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
  };

  const handleSidebarDragStart = (e, type) => {
    e.dataTransfer.setData("fieldType", type);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleMainDrop = (e) => {
    e.preventDefault();
    const type = e.dataTransfer.getData("fieldType");
    if (type) {
      handleOpenAdd(type);
    }
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newFields = [...fieldsList];
    const draggedItem = newFields[draggedIndex];
    newFields.splice(draggedIndex, 1);
    newFields.splice(index, 0, draggedItem);

    setFieldsList(newFields);
    setDraggedIndex(null);
  };

  const handleSaveSequence = async () => {
    try {
      const session = getSession();
      const currentToken = session.TokenId || localStorage.getItem('TokenId') || '-2295521862261168';
      const currentParentId = session.parentId || localStorage.getItem('ParentID') || localStorage.getItem('userId') || '0';

      const sequence = fieldsList.map(f => f.field_ID || f.FieldID || f.field_id).join(',');

      const payload = {
        Token: currentToken,
        Details: {
          ParentId: String(currentParentId),
          FieldSequence: sequence
        }
      };

      const response = await axios.post(`${NEWV3_BASE_URL}${API_ENDPOINTS.ENQUIRIES.SAVE_FIELD_SEQUENCE}`, payload);
      if (response.data && response.data.Status === 1) {
        setAlertConfig({
          show: true,
          type: 'success',
          title: 'Success',
          message: 'Field sequence saved successfully',
          onConfirm: () => {
            setAlertConfig(prev => ({ ...prev, show: false }));
            fetchFields(tabValue === 0 ? 'Active' : 'Inactive');
          }
        });
      } else {
        toast.error(response.data?.Message || "Failed to save sequence");
      }
    } catch (error) {
      console.error('Error saving sequence:', error);
      toast.error('Error saving sequence');
    }
  };

  const handleAddChoice = () => {
    setChoiceList([...choiceList, { id: Date.now(), value: '' }]);
  };

  const handleRemoveChoice = (id) => {
    if (choiceList.length > 1) {
      setChoiceList(choiceList.filter(c => c.id !== id));
    }
  };

  const handleChoiceChange = (id, value) => {
    setChoiceList(choiceList.map(c => c.id === id ? { ...c, value } : c));
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenAdd = (type) => {
    setModalMode('add');
    setDataType(type);
    setSelectedFieldTypeLabel(FIELD_TYPES.find(f => f.type === type)?.label || 'Custom Field');

    // Reset form
    setFieldId(0);
    setFieldName('');
    setIsMandatory(false);
    setIsQuickAdd(true);
    setIsActive(true);
    setDefaultValue('');
    
    // For numeric/date fields, use appropriate defaults
    if (type === 'f') {
      setMinVal('0');
      setMaxVal('0');
    } else if (type === 'd') {
      setMinVal('');
      setMaxVal('');
    } else {
      setMinVal('0');
      setMaxVal('0');
    }

    setChoices('');
    setChoiceList([{ id: Date.now(), value: '' }]);
    setChoices('');
    setChoiceList([{ id: Date.now(), value: '' }]);
    setTooltip('');
    setIsCurrentDateDefault(false);
    setFormula('');

    // Formula info
    setSelectedFormulaId('');
    setParamMappings([]);
    setCurrentFormulaParams([]);

    // Dependency Info
    setParentFieldId('');
    setChildFieldId('');
    setMappedValues({});
    setParentFields([]);
    setChildFields([]);
    setFormulaParams([]);
    setDrawerTabValue(0);
    setAddedMappings([]);
    setIsDepModalOpen(false);
    setDepTabValue(0);

    setIsModalOpen(true);
  };

  const handleOpenEdit = (field) => {
    const rawType = field.datatype || field.DataType || 'v';
    const typeObj = FIELD_TYPES.find(f => f.type === rawType || f.label === rawType);
    const type = typeObj ? typeObj.type : 'v';

    setModalMode('edit');
    setDataType(type);
    setSelectedFieldTypeLabel(typeObj?.label || 'Custom Field');

    setFieldId(field.field_ID || field.FieldID || field.Id || 0);
    setFieldName(field.fieldname || field.FieldName || '');
    setIsMandatory(field.isMandatory === true || field.IsMandatory === 'True' || String(field.IsMandatory).toLowerCase() === 'true');
    setIsQuickAdd(field.quickAdd === true || field.IsQuickAdd === 'True' || String(field.IsQuickAdd).toLowerCase() === 'true');
    setIsActive(field.isActive === true || field.IsActive === 'True' || String(field.IsActive).toLowerCase() === 'true');
    setDefaultValue(field.defaultValue || field.DefaultValue || '');
    
    const min = field.minvalue || field.MinValue || '';
    const max = field.maxvalue || field.MaxValue || '';
    setMinVal(min === '0' && type === 'd' ? '' : min);
    setMaxVal(max === '0' && type === 'd' ? '' : max);
    
    const choicesVal = field.choices || field.Choices || '';
    setChoices(choicesVal);
    if (choicesVal && type !== 'e') {
      setChoiceList(choicesVal.split(',').map((v, i) => ({ id: i, value: v.trim() })));
    } else {
      setChoiceList([{ id: Date.now(), value: '' }]);
    }
    setTooltip(field.tooltip || field.Tooltip || '');
    setFormula(type === 'e' ? choicesVal : '');
    setIsCurrentDateDefault(field.currentDate === true || field.IsCurrentDate === 'True' || String(field.IsCurrentDate).toLowerCase() === 'true');

    // Load dependency info
    setDrawerTabValue(0);
    const pcId = field.pcFieldId || field.PCFieldId || ''; // This is the CHILD ID (if I am Parent)
    const cpId = field.cpFieldId || field.CPFieldId || ''; // This is the PARENT ID (if I am Child)
    
    setChildFieldId(pcId);
    setParentFieldId(cpId);
    
    const mappings = [];
    if (cpId) {
       mappings.push({ id: 'p-' + Date.now(), text: field.Parent || field.ParentFieldName || cpId, type: 'Parent' });
       fetchParentChoices(cpId, 'Parent');
    }
    if (pcId) {
       mappings.push({ id: 'c-' + Date.now(), text: field.Child || field.ChildFieldName || pcId, type: 'Child' });
    }
    setAddedMappings(mappings);
    
    if (cpId) {
       setActiveDependencyMode('Parent');
    } else if (pcId) {
       setActiveDependencyMode('Child');
    }

    const pDep = field.pDependencyJson || field.PDependencyJson || '[]';
    const cDep = field.cDependencyJson || field.CDependencyJson || '[]';
    
    try {
      const pDepObj = JSON.parse(pDep);
      const cDepObj = JSON.parse(cDep);
      
      const newMappedValues = {};
      if (pDepObj && pDepObj.length > 0) {
        pDepObj[0].ParentChildMapping.forEach(m => {
          newMappedValues[m.ParentValue] = typeof m.ChildValue === 'string' ? m.ChildValue.split(',').map(s => s.trim()).filter(s => s) : (Array.isArray(m.ChildValue) ? m.ChildValue : []);
        });
      } else if (cDepObj && cDepObj.length > 0) {
        cDepObj.forEach(m => {
          newMappedValues[m.ParentValue] = typeof m.ChildValue === 'string' ? m.ChildValue.split(',').map(s => s.trim()).filter(s => s) : (Array.isArray(m.ChildValue) ? m.ChildValue : []);
        });
      }
      setMappedValues(newMappedValues);
    } catch (e) {
      console.error("Error parsing dependency JSON", e);
      setMappedValues({});
    }

    // Load formula mapping if edit mode and calc
    if (type === 'e') {
        const formulaName = field.choices || field.Choices || '';
        const fObj = formulaList.find(f => f.Name === formulaName || f.DisplayName === formulaName);
        if (fObj) setSelectedFormulaId(fObj.Id);
    }

    setIsModalOpen(true);
  };

  const validateField = () => {
    console.log("Validating field... Name:", fieldName, "Type:", dataType);

    // 1. Basic Field Count Limit (Removed per user request to avoid "Field count limit" error)
    /*
    if (fieldsList.length >= 40) {
      console.log("Validation failed: Field count limit");
      toast.error("Maximum of only 40 Custom fields are allowed");
      return false;
    }
    */

    // 2. Field Name Validation
    if (!fieldName || !fieldName.trim()) {
      console.log("Validation failed: Empty name");
      setAlertConfig({
        show: true,
        type: 'error',
        title: 'Validation Error',
        message: "Field name is required",
        onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
      });
      return false;
    }

    // Reserved Keyword check
    if (RESERVED_KEYWORDS.includes(fieldName.toLowerCase())) {
      console.log("Validation failed: Reserved keyword -", fieldName);
      setAlertConfig({
        show: true,
        type: 'error',
        title: 'Validation Error',
        message: `"${fieldName}" is a reserved system keyword. Please use a different name (e.g., Custom_${fieldName}).`,
        onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
      });
      return false;
    }

    // Duplicate Name Check (excluding current field being edited)
    const isDuplicate = fieldsList.some(f => 
      (f.fieldname || f.FieldName || '').toLowerCase() === fieldName.toLowerCase() && 
      (f.field_ID || f.FieldID || f.Id || 0) !== (fieldId || 0)
    );
    if (isDuplicate) {
      console.log("Validation failed: Duplicate name -", fieldName);
      setAlertConfig({
        show: true,
        type: 'error',
        title: 'Validation Error',
        message: `Field name "${fieldName}" already exists. Please use a unique name.`,
        onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
      });
      return false;
    }

    // Regex check: Only Alphabets, Numbers & Underscore, first char not a number, no spaces
    const regexFldName = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
    if (!regexFldName.test(fieldName)) {
      console.log("Validation failed: Regex failed");
      setAlertConfig({
        show: true,
        type: 'error',
        title: 'Validation Error',
        message: "Please enter only Alphabets, Numbers & (_)Underscore. First character should not be a number and space is also not allowed.",
        onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
      });
      return false;
    }

    // 3. Numeric Field Validation (dataType 'f')
    if (dataType === 'f') {
      const min = parseFloat(minVal);
      const max = parseFloat(maxVal);
      const def = parseFloat(defaultValue);

      if (minVal !== '' && maxVal !== '') {
        if (isNaN(min) || isNaN(max)) {
          console.log("Validation failed: Invalid numeric min/max");
          toast.error("Minimum and Maximum values must be valid numbers");
          return false;
        }
        if (min > max) {
          console.log("Validation failed: min > max");
          setAlertConfig({
            show: true,
            type: 'error',
            title: 'Validation Error',
            message: "Minimum value should not be greater than Maximum value",
            onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
          });
          return false;
        }
        if (defaultValue !== '' && !isNaN(def)) {
          if (def < min || def > max) {
            console.log("Validation failed: default out of range");
            toast.error("Default value should be in range of Minimum and Maximum");
            return false;
          }
        }
      }
    }

    // 4. Date Field Validation (dataType 'd')
    if (dataType === 'd') {
      if (minVal && maxVal) {
        const dateMin = new Date(minVal);
        const dateMax = new Date(maxVal);
        if (dateMin > dateMax) {
          console.log("Validation failed: date min > max");
          toast.error("Minimum date value should not be greater than Maximum date value");
          return false;
        }
        if (defaultValue) {
          const dateDef = new Date(defaultValue);
          if (dateDef < dateMin || dateDef > dateMax) {
            console.log("Validation failed: date default out of range");
            toast.error("Default date value should be in range of Min/Max dates");
            return false;
          }
        }
      }
    }

    // 5. Choice Fields Validation
    const filteredChoices = (choiceList || []).map(c => (c.value || '').trim()).filter(v => v !== '');
    const choiceFields = ['r', 'c']; // Removed ddl and mddl as choice is not required for them per user request
    if (choiceFields.includes(dataType)) {
      if (filteredChoices.length === 0) {
        console.log("Validation failed: No choices");
        setAlertConfig({
          show: true,
          type: 'error',
          title: 'Validation Error',
          message: "Choice is required for this field type.",
          onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
        });
        return false;
      }
    }

    if (dataType === 'r' || dataType === 's') {
      if (defaultValue && !filteredChoices.some(c => c.toLowerCase() === defaultValue.toLowerCase())) {
        console.log("Validation failed: Default choice invalid");
        setAlertConfig({
          show: true,
          type: 'error',
          title: 'Validation Error',
          message: "Default value must be one of the choices.",
          onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
        });
        return false;
      }
      if (dataType === 'r' && !defaultValue) {
        console.log("Validation failed: Radio default required");
        setAlertConfig({
          show: true,
          type: 'error',
          title: 'Validation Error',
          message: "Default value is required for Radio Button fields.",
          onConfirm: () => setAlertConfig(prev => ({ ...prev, show: false }))
        });
        return false;
      }
    }

    // 6. Formula Validation
    if (dataType === 'e') {
      if (!formula) {
        console.log("Validation failed: No formula");
        toast.error("Please select a formula");
        return false;
      }
      if (!defaultValue) {
        console.log("Validation failed: No formula default");
        toast.error("Please provide a default value for the formula");
        return false;
      }
    }

    console.log("Validation SUCCESS");
    return true;
  };

  const handleSave = async () => {
    try {
      console.log("handleSave triggered. Mode:", modalMode);

      if (!validateField()) {
        console.log("Validation failed");
        return;
      }

      setIsSubmitting(true);
      
      // Refresh session data inside save to ensure current values
      const session = getSession();
      const currentToken = session.TokenId || localStorage.getItem('TokenId') || '-2295521862261168';
      const currentParentId = session.parentId || localStorage.getItem('ParentID') || localStorage.getItem('userId') || '0';

      console.log("Validation passed. parentId:", currentParentId, "TokenId:", currentToken);

      // Construct choices based on type
      let finalChoices = '';
      if (dataType === 'e') {
        finalChoices = formula || '';
      } else if (['r', 's', 'l', 'c'].includes(dataType)) {
        finalChoices = (choiceList || [])
          .map(c => (c.value || '').trim())
          .filter(v => v !== '')
          .join(',');
      }

      const payload = {
        Token: currentToken,
        Details: {
          ParentId: String(currentParentId),
          FieldID: fieldId || 0,
          FieldName: fieldName || '',
          DataType: dataType || 'v',
          MinValue: (dataType === 'f' || dataType === 'd') ? (minVal || '0') : '0',
          MaxValue: (dataType === 'f' || dataType === 'd') ? (maxVal || '0') : '0',
          DefaultValue: defaultValue || '',
          IsActive: isActive ? "True" : "False",
          IsMandatory: isMandatory ? "True" : "False",
          IsQuickAdd: isQuickAdd ? "True" : "False",
          IsCurrentDate: (dataType === 'd' && isCurrentDateDefault) ? "True" : "False",
          Tooltip: tooltip || '',
          Choices: finalChoices,
          JSONchoices: JSON.stringify(choiceList.map(c => ({ Id: c.id, Value: c.value }))),
          PDependencyJson: childFieldId ? JSON.stringify([{
            FieldId: childFieldId,
            ParentChildMapping: Object.keys(mappedValues).map(pVal => ({
              ParentValue: pVal,
              ChildValue: mappedValues[pVal]
            }))
          }]) : "[]",
          CDependencyJson: parentFieldId ? JSON.stringify(Object.keys(mappedValues).map(pVal => ({
            ParentValue: pVal,
            ChildValue: mappedValues[pVal]
          }))) : "[]",
          PCFieldId: childFieldId || '',
          CPFieldId: parentFieldId || ''
        }
      };

      console.log("Payload prepared:", JSON.stringify(payload));

      const endpoint = modalMode === 'add'
        ? API_ENDPOINTS.ENQUIRIES.ADD_CUSTOM_FIELD
        : API_ENDPOINTS.ENQUIRIES.UPDATE_CUSTOM_FIELD;

      const fullUrl = `${NEWV3_BASE_URL}${endpoint}`;
      console.log("Calling API:", fullUrl);

      const response = await axios.post(fullUrl, payload);
      console.log("API Response received:", response.data);

      if (response.data && response.data.Status === 1) {
        const savedFieldId = response.data.Details || fieldId;

        // Save formula parameters if calc
        if (dataType === 'e' && paramMappings.length > 0) {
          const mappingPayload = {
            Token: currentToken,
            Details: {
              FieldId: savedFieldId,
              ParentId: String(currentParentId),
              ParamList: paramMappings
            }
          };
          await axios.post(`${NEWV3_BASE_URL}/UserCRM/AddcustomFieldFormulaPM`, mappingPayload);
        }

        setAlertConfig({
          show: true,
          type: 'success',
          title: 'Success',
          message: `Field ${modalMode === 'add' ? 'created' : 'updated'} successfully`,
          onConfirm: () => {
            setAlertConfig(prev => ({ ...prev, show: false }));
            setIsModalOpen(false);
            fetchFields(tabValue === 0 ? 'Active' : 'Inactive');
          }
        });
      } else {
        const errorMsg = response.data?.Message || "Operation failed";
        console.error("Save failed with message:", errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error("CRITICAL error in handleSave:", error);
      const msg = error.response?.data?.Message || error.message || "Unknown error";
      toast.error("Failed to save field: " + msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to determine if we show choices box
  const showChoices = ['r', 'ddl', 'mddl'].includes(dataType);
  // Helper to determine if we show min/max
  const showMinMax = ['f'].includes(dataType);

  return (
    <Box sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>

      {/* Header Area - Matching Image 2 */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        bgcolor: '#0084d6',
        p: '10px 20px',
        color: '#fff'
      }}>
        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 500, color: '#ffffff !important' }}>Kit19</Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="contained"
            sx={{
              bgcolor: '#195a8e',
              '&:hover': { bgcolor: '#144a76' },
              textTransform: 'none',
              fontSize: '0.85rem',
              fontWeight: 400,
              borderRadius: '4px',
              px: 2
            }}
            onClick={handleSaveSequence}
          >
            Save Field Order
          </Button>
          <Button
            variant="contained"
            sx={{
              bgcolor: '#f39c12',
              '&:hover': { bgcolor: '#e67e22' },
              textTransform: 'none',
              fontSize: '0.85rem',
              fontWeight: 400,
              borderRadius: '4px',
              px: 2
            }}
            onClick={onClose}
          >
            Close
          </Button>
        </Box>
      </Box>

      {/* Tabs Area - White Bar below Header */}
      <Box sx={{ borderBottom: 1, borderColor: '#eee', px: 2, bgcolor: '#fff' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            minHeight: '40px',
            '& .MuiTab-root': {
              textTransform: 'none',
              minHeight: '40px',
              fontSize: '0.9rem',
              color: '#555',
              fontWeight: 500
            },
            '& .Mui-selected': {
              color: '#0084d6 !important'
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#0084d6'
            }
          }}
        >
          <Tab label="Active" />
          <Tab label="Inactive" />
        </Tabs>
      </Box>

      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>

        {/* Left Side: Field List */}
        <Box
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleMainDrop}
          sx={{ flexGrow: 1, p: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}
        >
          <Box sx={{ bgcolor: 'white', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, overflowY: 'auto', flexGrow: 1 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#333', fontSize: '1.1rem', fontWeight: 500 }}>
                {tabValue === 0 ? 'Active' : 'Inactive'} Fields
              </Typography>

              {fieldsList.map((field, index) => {
                const typeInfo = FIELD_TYPES.find(ft => ft.type === (field.datatype || field.DataType) || ft.label === (field.datatype || field.DataType));
                return (
                  <Box
                    key={field.field_ID || index}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      border: '1px solid #ddd',
                      mb: '8px',
                      bgcolor: draggedIndex === index ? '#f0f0f0' : 'white',
                      transition: 'background-color 0.2s',
                      '&:hover': { bgcolor: '#f9f9f9' }
                    }}
                  >
                    <Box sx={{ p: '8px 12px', display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                      <DragIndicatorIcon sx={{ mr: 1, color: '#ccc', cursor: 'grab', fontSize: '1.2rem' }} />
                      <Typography variant="body2" sx={{ color: '#333', fontSize: '0.85rem' }}>
                        {field.fieldname || field.FieldName}
                        <span style={{ color: '#777', marginLeft: '4px' }}>
                          ( Field Type: {typeInfo?.label || field.datatype || field.DataType}, Required: { (field.isMandatory === true || field.isMandatory === 'True' || String(field.isMandatory).toLowerCase() === 'true') ? 'True' : 'False' }, Quick Add: { (field.quickAdd === true || field.quickAdd === 'True' || String(field.quickAdd).toLowerCase() === 'true') ? 'True' : 'False' }, Default Value: {field.defaultValue || field.DefaultValue || ''}, Parent: {field.Parent || field.ParentFieldName || ''}, Child: {field.Child || field.ChildFieldName || ''} )
                        </span>
                      </Typography>
                    </Box>
                    <Box sx={{ borderLeft: '1px solid #ddd', alignSelf: 'stretch', display: 'flex', alignItems: 'center', px: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEdit(field)}
                        sx={{
                          color: '#0084d6',
                          '&:hover': { bgcolor: 'rgba(0,132,214,0.08)' }
                        }}
                      >
                        <EditIcon sx={{ fontSize: '1.2rem' }} />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}

            </Box>
          </Box>
        </Box>

        {/* Right Side: Add Field Buttons - Below main header like Image 3 */}
        <Box sx={{
          width: '280px',
          flexShrink: 0,
          bgcolor: '#f5f7fa',
          p: 2,
          borderLeft: '1px solid #e0e0e0',
          overflowY: 'auto'
        }}>
          <Typography variant="subtitle2" sx={{ mb: 2, color: '#333', fontWeight: 600, fontSize: '0.75rem' }}>
            ADD CUSTOM FIELD
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {FIELD_TYPES.map((ft) => (
              <Button
                key={ft.type}
                variant="outlined"
                fullWidth
                draggable
                onDragStart={(e) => handleSidebarDragStart(e, ft.type)}
                sx={{
                  py: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: 'white',
                  borderColor: '#ddd',
                  color: '#555',
                  textTransform: 'none',
                  cursor: 'grab',
                  '&:hover': { bgcolor: '#f0f0f0' },
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}
                onClick={() => handleOpenAdd(ft.type)}
              >
                <Box sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
                  {ft.icon}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{ft.label}</Typography>
              </Button>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Drawer for Add/Edit - Matching Image 3 style, positioned below header */}
  <Drawer
    anchor="right"
    open={isModalOpen}
    onClose={() => setIsModalOpen(false)}
    sx={{
      zIndex: 10000,
      '& .MuiDrawer-paper': {
        top: '55px', 
        height: 'calc(100% - 55px)',
        width: 500,
        boxShadow: '-2px 0 8px rgba(0,0,0,0.1)'
      },
      '& .MuiBackdrop-root': {
        top: '55px'
      }
    }}
  >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '15px 20px', borderBottom: '1px solid #eee' }}>
            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 500, color: '#333' }}>
              {modalMode === 'add'
                ? `Add New ${selectedFieldTypeLabel}`
                : `Edit ${selectedFieldTypeLabel}`}
            </Typography>
            <IconButton onClick={() => setIsModalOpen(false)} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Tabs removed per user request for simplicity */}

          {/* Content */}
          <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#333', fontWeight: 600 }}>Field Name</Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={fieldName}
                    onChange={(e) => setFieldName(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '2px' } }}
                  />
                </Box>

            {(dataType === 'f' || dataType === 'd') && (
              <>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#333', fontWeight: 600 }}>
                    {dataType === 'd' ? "Minimum Date" : "Minimum Value"}
                  </Typography>
                  <TextField
                    type={dataType === 'd' ? "date" : "text"}
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={minVal}
                    onChange={(e) => setMinVal(e.target.value)}
                    onKeyPress={(e) => {
                      if (dataType === 'f' && !/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '2px' } }}
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#333', fontWeight: 600 }}>
                    {dataType === 'd' ? "Maximum Date" : "Maximum Value"}
                  </Typography>
                  <TextField
                    type={dataType === 'd' ? "date" : "text"}
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={maxVal}
                    onChange={(e) => setMaxVal(e.target.value)}
                    onKeyPress={(e) => {
                      if (dataType === 'f' && !/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '2px' } }}
                  />
                </Box>
              </>
            )}

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#333', fontWeight: 600 }}>Default Value</Typography>
              <TextField
                type={dataType === 'd' ? "date" : "text"}
                variant="outlined"
                fullWidth
                size="small"
                multiline={dataType === 'm'}
                rows={dataType === 'm' ? 3 : 1}
                value={defaultValue}
                onChange={(e) => setDefaultValue(e.target.value)}
                disabled={dataType === 'd' && isCurrentDateDefault}
                onKeyPress={(e) => {
                  if (dataType === 'f' && !/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '2px' } }}
              />
            </Box>

            {dataType === 'e' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#333', fontWeight: 600 }}>Formula</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      minWidth: 45,
                      height: 40,
                      border: '1px solid #ccc',
                      borderRight: 'none',
                      borderRadius: '2px 0 0 2px',
                      backgroundColor: '#f9f9f9'
                    }}>
                      <Typography sx={{ fontWeight: 'normal', fontSize: '1.2rem', color: '#333' }}>Σ</Typography>
                    </Box>
                    <Select
                      fullWidth
                      size="small"
                      value={selectedFormulaId || ""}
                      onChange={(e) => {
                        const fId = e.target.value;
                        if (!fId) return;
                        setSelectedFormulaId(fId);
                        const selectedFormula = formulaList.find(f => String(f.Id) === String(fId));
                        if (selectedFormula) {
                          setFormula(selectedFormula.Name);
                          fetchFormulaParamsByFormulaId(fId);
                        }
                      }}
                      displayEmpty
                      MenuProps={{ style: { zIndex: 11000 } }}
                      sx={{ 
                        borderRadius: '0 2px 2px 0',
                        bgcolor: '#fff',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderLeft: 'none'
                        }
                      }}
                    >
                      <MenuItem value="">--Select Formula--</MenuItem>
                      {formulaList.map(opt => (
                        <MenuItem key={opt.Id} value={String(opt.Id)}>{opt.DisplayName}</MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Box>
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#333', fontWeight: 600 }}>Tooltip Value</Typography>
              <TextField
                variant="outlined"
                fullWidth
                size="small"
                value={tooltip}
                onChange={(e) => setTooltip(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '2px' } }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <FormControlLabel
                control={<Checkbox checked={isMandatory} onChange={(e) => setIsMandatory(e.target.checked)} size="small" color="success" sx={{ p: 0.5 }} />}
                label={<Typography sx={{ fontSize: '0.85rem', color: '#333' }}>Mark this field as required</Typography>}
              />
              <FormControlLabel
                control={<Checkbox checked={isQuickAdd} onChange={(e) => setIsQuickAdd(e.target.checked)} size="small" color="success" sx={{ p: 0.5 }} />}
                label={<Typography sx={{ fontSize: '0.85rem', color: '#333' }}>Show this field in quick add</Typography>}
              />
              <FormControlLabel
                control={<Checkbox checked={isActive} onChange={(e) => setIsActive(e.target.checked)} size="small" color="success" sx={{ p: 0.5 }} />}
                label={<Typography sx={{ fontSize: '0.85rem', color: '#333' }}>Is Active</Typography>}
              />
            </Box>

            {['s', 'l', 'r'].includes(dataType) && (
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Parent Of Section */}
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#333' }}>Parent Of</Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        setActiveDependencyMode('Child');
                        setIsDepModalOpen(true);
                        setDepTabValue(0);
                        fetchDepChildFields("Child");
                      }}
                      sx={{ color: '#2196f3', p: 0 }}
                    >
                      <AddCircleIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {addedMappings.filter(m => m.type === 'Child').map(m => (
                      <Card 
                        key={m.id} 
                        variant="elevation" 
                        elevation={1}
                        sx={{ 
                          p: '10px 15px', 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          borderRadius: '0px',
                          border: '1px solid #eee',
                          mb: '2px'
                        }}
                      >
                        <Typography sx={{ fontSize: '0.9rem', color: '#333' }}>{m.text}</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton 
                            size="small" 
                            sx={{ color: '#666' }}
                            onClick={() => {
                              setActiveDependencyMode('Child');
                              setIsDepModalOpen(true);
                              setDepTabValue(1); // Go to map values tab
                              setSelectedDepChildId(m.text); // Assuming m.text is the field name or ID
                              fetchDepChildValues(m.text);
                            }}
                          >
                            <EditIcon sx={{ fontSize: '1.2rem' }} />
                          </IconButton>
                          <IconButton size="small" sx={{ color: '#666' }} onClick={() => setAddedMappings(addedMappings.filter(x => x.id !== m.id))}>
                            <DeleteIcon sx={{ fontSize: '1.2rem' }} />
                          </IconButton>
                        </Box>
                      </Card>
                    ))}
                  </Box>
                </Box>

                {/* Child of Section */}
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#333' }}>Child of</Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        setActiveDependencyMode('Parent');
                        setIsDepModalOpen(true);
                        setDepTabValue(0);
                        fetchDepChildFields("Parent");
                      }}
                      sx={{ color: '#666', p: 0 }}
                    >
                      <AddCircleIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {addedMappings.filter(m => m.type === 'Parent').map(m => (
                      <Card 
                        key={m.id} 
                        variant="elevation" 
                        elevation={1}
                        sx={{ 
                          p: '10px 15px', 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          borderRadius: '0px',
                          border: '1px solid #eee',
                          mb: '2px'
                        }}
                      >
                        <Typography sx={{ fontSize: '0.9rem', color: '#333' }}>{m.text}</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton 
                            size="small" 
                            sx={{ color: '#666' }}
                            onClick={() => {
                              setActiveDependencyMode('Parent');
                              setIsDepModalOpen(true);
                              setDepTabValue(1); // Go to map values tab
                              setSelectedDepChildId(m.text);
                              fetchDepChildValues(m.text);
                            }}
                          >
                            <EditIcon sx={{ fontSize: '1.2rem' }} />
                          </IconButton>
                          <IconButton size="small" sx={{ color: '#666' }} onClick={() => setAddedMappings(addedMappings.filter(x => x.id !== m.id))}>
                            <DeleteIcon sx={{ fontSize: '1.2rem' }} />
                          </IconButton>
                        </Box>
                      </Card>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}

            {dataType === 'd' && (
                <FormControlLabel
                  control={<Checkbox
                    checked={isCurrentDateDefault}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIsCurrentDateDefault(checked);
                      if (checked) {
                        setMinVal('');
                        setMaxVal('');
                        setDefaultValue('');
                      }
                    }}
                    size="small"
                    color="success"
                    sx={{ p: 0.5 }}
                  />}
                  label={<Typography sx={{ fontSize: '0.85rem', color: '#333' }}>Get current date as default</Typography>}
                />
              )}

            {(dataType === 's' || dataType === 'l' || dataType === 'r' || dataType === 'c') && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle2" sx={{ color: '#333', fontWeight: 600 }}>Choice</Typography>
                {choiceList.map((choice) => (
                  <Box key={choice.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DragIndicatorIcon sx={{ color: '#777', fontSize: '1.2rem', cursor: 'grab' }} />
                    <IconButton size="small" sx={{ p: 0.5, color: '#d32f2f' }} onClick={() => handleRemoveChoice(choice.id)}>
                      <RemoveCircleIcon sx={{ fontSize: '1.2rem' }} />
                    </IconButton>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Choice Value"
                      value={choice.value}
                      onChange={(e) => handleChoiceChange(choice.id, e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '2px' } }}
                    />
                  </Box>
                ))}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    startIcon={<AddCircleIcon />}
                    onClick={handleAddChoice}
                    sx={{ textTransform: 'none', color: '#1976d2', fontSize: '0.8rem' }}
                  >
                    Add Choice
                  </Button>
                  <Button
                    startIcon={<PlaylistAddCheckIcon />}
                    onClick={() => {
                      const bulk = prompt("Enter choices separated by commas:");
                      if (bulk) {
                        const newChoices = bulk.split(',').map(v => ({ id: Date.now() + Math.random(), value: v.trim() })).filter(c => c.value !== '');
                        setChoiceList([...choiceList, ...newChoices]);
                      }
                    }}
                    sx={{ textTransform: 'none', color: '#4caf50', fontSize: '0.8rem' }}
                  >
                    Bulk Add
                  </Button>
                </Box>
              </Box>
            )}
          </Box>

          {/* Footer - Matching Image 3 style */}
          <Box sx={{ p: 2, borderTop: '1px solid #eee', display: 'flex', gap: 1.5 }}>
            <Button
              variant="contained"
              disabled={isSubmitting}
              sx={{
                bgcolor: '#4caf50',
                '&:hover': { bgcolor: '#43a047' },
                textTransform: 'none',
                borderRadius: '2px',
                minWidth: '100px',
                boxShadow: 'none'
              }}
              onClick={handleSave}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="contained"
              sx={{
                bgcolor: '#e0e0e0',
                color: '#333',
                '&:hover': { bgcolor: '#d5d5d5' },
                textTransform: 'none',
                borderRadius: '2px',
                minWidth: '100px',
                boxShadow: 'none'
              }}
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Formula Parameter Modal - Matching Image 3 */}
      <Dialog 
        open={isParamModalOpen} 
        onClose={() => setIsParamModalOpen(false)}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: 12000 }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee', fontSize: '1rem', py: 1.5 }}>
          {formulaList.find(f => String(f.Id) === String(selectedFormulaId))?.DisplayName || 'Formula'}
        </DialogTitle>
        <DialogContent sx={{ p: 2, pt: 3 + ' !important' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {paramMappings.map((mapping, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2, border: '1px solid #eee', p: 1, borderRadius: '4px' }}>
                <Typography variant="body2" sx={{ minWidth: 80, fontWeight: 500 }}>{mapping.ParamName}</Typography>
                
                {mapping.ParamType === 'Field' ? (
                  <Select
                    fullWidth
                    size="small"
                    value={mapping.ParamValue}
                    onChange={(e) => {
                      const newMappings = [...paramMappings];
                      newMappings[idx].ParamValue = e.target.value;
                      setParamMappings(newMappings);
                    }}
                    MenuProps={{ style: { zIndex: 13000 } }}
                    sx={{ borderRadius: '2px' }}
                  >
                    <MenuItem value="">--Select Field--</MenuItem>
                    {allCrmFields.map(f => (
                      <MenuItem key={f.ColumnName} value={f.ColumnName}>{f.DisplayName}</MenuItem>
                    ))}
                  </Select>
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Value"
                    value={mapping.ParamValue}
                    onChange={(e) => {
                      const newMappings = [...paramMappings];
                      newMappings[idx].ParamValue = e.target.value;
                      setParamMappings(newMappings);
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '2px' } }}
                  />
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                   <Typography variant="caption" sx={{ color: mapping.ParamType === 'Field' ? '#999' : '#0084d6' }}>OFF</Typography>
                   <Switch 
                      size="small" 
                      checked={mapping.ParamType === 'Field'} 
                      onChange={(e) => {
                         const newMappings = [...paramMappings];
                         newMappings[idx].ParamType = e.target.checked ? 'Field' : 'Static';
                         newMappings[idx].ParamValue = '';
                         setParamMappings(newMappings);
                      }}
                   />
                   <Typography variant="caption" sx={{ color: mapping.ParamType === 'Field' ? '#0084d6' : '#999' }}>ON</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #eee', p: 1.5 }}>
          <Button 
            onClick={() => setIsParamModalOpen(false)} 
            size="small"
            sx={{ textTransform: 'none', color: '#666' }}
            startIcon={<CloseIcon />}
          >
            Close
          </Button>
          <Button 
            variant="contained" 
            onClick={() => setIsParamModalOpen(false)} 
            size="small"
            sx={{ textTransform: 'none', bgcolor: '#195a8e' }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Map Dependent field Modal - Matching Image 3 & 4 */}
      <Dialog 
        open={isDepModalOpen}
        onClose={() => setIsDepModalOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: 'fit-content',
            minWidth: '600px',
            maxWidth: '95vw',
            m: 2
          }
        }}
        sx={{ zIndex: 11000 }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee', fontSize: '1rem', py: 1.5 }}>
          Map Dependent field
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ borderBottom: 1, borderColor: '#eee' }}>
            <Tabs value={depTabValue} onChange={(e, v) => setDepTabValue(v)} sx={{ minHeight: 40 }}>
              <Tab label="Parent & Child field" sx={{ textTransform: 'none', fontSize: '0.85rem' }} />
              <Tab label="Map values" sx={{ textTransform: 'none', fontSize: '0.85rem' }} disabled={!selectedDepChildId} />
            </Tabs>
          </Box>
          <Box sx={{ p: 3 }}>
            {depTabValue === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#333', fontWeight: 600 }}>
                    {activeDependencyMode === 'Parent' ? 'Select Parent Field' : 'Select Parent Field'}
                  </Typography>
                  {activeDependencyMode === 'Parent' ? (
                    <Select
                      fullWidth
                      size="small"
                      value={selectedDepChildId}
                      onChange={(e) => {
                        const pId = e.target.value;
                        setSelectedDepChildId(pId);
                        fetchParentChoices(pId);
                      }}
                      displayEmpty
                      MenuProps={{ style: { zIndex: 12000 } }}
                    >
                      <MenuItem value="">Nothing selected</MenuItem>
                      {depChildFields.map(f => (
                        <MenuItem key={f.Id} value={String(f.Id)}>{f.Text}</MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <TextField
                      fullWidth
                      size="small"
                      value={fieldName || "new_dropdown"}
                      disabled
                      sx={{ bgcolor: '#f5f5f5' }}
                    />
                  )}
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#333', fontWeight: 600 }}>
                    {activeDependencyMode === 'Parent' ? 'Select Child Field' : 'Select Child Field'}
                  </Typography>
                  {activeDependencyMode === 'Parent' ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={fieldName || "new_dropdown"}
                      disabled
                      sx={{ bgcolor: '#f5f5f5' }}
                    />
                  ) : (
                    <Select
                      fullWidth
                      size="small"
                      value={selectedDepChildId}
                      onChange={(e) => {
                        const cId = e.target.value;
                        setSelectedDepChildId(cId);
                        fetchDepChildValues(cId);
                      }}
                      displayEmpty
                      MenuProps={{ style: { zIndex: 12000 } }}
                    >
                      <MenuItem value="">Nothing selected</MenuItem>
                      {depChildFields.map(f => (
                        <MenuItem key={f.Id} value={String(f.Id)}>{f.Text}</MenuItem>
                      ))}
                    </Select>
                  )}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button 
                    variant="contained" 
                    color="success" 
                    onClick={() => {
                      setDepTabValue(1);
                      fetchMappingChoices(selectedDepChildId);
                      fetchDepChildValues(selectedDepChildId);
                    }}
                    disabled={!selectedDepChildId}
                    sx={{ textTransform: 'none', borderRadius: '2px', px: 4, bgcolor: '#28a745', '&:hover': { bgcolor: '#218838' } }}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button size="small" sx={{ textTransform: 'none', minWidth: 0, p: 0, color: '#1976d2', fontSize: '0.8rem' }} onClick={() => setDepTabValue(0)}>Previous</Button>
                  <Typography variant="caption" sx={{ color: '#ccc' }}>|</Typography>
                  <Button size="small" sx={{ textTransform: 'none', minWidth: 0, p: 0, color: '#1976d2', fontSize: '0.8rem' }}>Next</Button>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1, '&::-webkit-scrollbar': { height: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#ccc', borderRadius: 3 } }}>
                  {(parentChoices.length > 0 ? parentChoices : [{Text: 'Diploma'}, {Text: 'Gratuate'}, {Text: 'Post Gratuate'}]).map((choice, cIdx) => (
                    <Box key={cIdx} sx={{ minWidth: 180, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#333', fontWeight: 'bold', fontSize: '0.85rem' }}>Select Parent Value</Typography>
                        <Select
                          fullWidth
                          size="small"
                          value={choice.Text}
                          onChange={(e) => {
                            // Update the parent choice if needed
                          }}
                          sx={{ 
                            mt: 0.5,
                            bgcolor: '#f2f2f2', 
                            backgroundImage: 'linear-gradient(to bottom, #ffffff, #e6e6e6)',
                            '& .MuiOutlinedInput-root': { borderRadius: '4px' },
                            '& .MuiSelect-select': { py: '6px' }
                          }}
                          MenuProps={{ style: { zIndex: 12000 } }}
                        >
                          <MenuItem value={choice.Text}>{choice.Text}</MenuItem>
                          {/* Add other choices if available */}
                        </Select>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#333', fontWeight: 'bold', fontSize: '0.85rem' }}>Select Child Values</Typography>
                        <Select
                          multiple
                          fullWidth
                          size="small"
                          value={mappedValues[choice.Text] || []}
                          onChange={(e) => {
                            const val = e.target.value;
                            setMappedValues(prev => ({ ...prev, [choice.Text]: val }));
                          }}
                          displayEmpty
                          sx={{ 
                            mt: 0.5,
                            bgcolor: '#f2f2f2', 
                            backgroundImage: 'linear-gradient(to bottom, #ffffff, #e6e6e6)',
                            borderRadius: '4px',
                            '& .MuiSelect-select': { py: '6px' }
                          }}
                          renderValue={(selected) => selected.length > 0 ? (selected.length > 1 ? `${selected.length} items` : selected[0]) : "Nothing selected"}
                          MenuProps={{ style: { zIndex: 12000 } }}
                        >
                          <MenuItem value="" disabled>Nothing selected</MenuItem>
                          {depChildValues.map(v => (
                            <MenuItem key={v.Id} value={v.Text}>{v.Text}</MenuItem>
                          ))}
                        </Select>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#333', fontWeight: 'bold', fontSize: '0.85rem' }}>Select Default Values</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Select default values"
                          sx={{ 
                            mt: 0.5,
                            '& .MuiOutlinedInput-root': { 
                              borderRadius: '4px',
                              bgcolor: '#fff'
                            },
                            '& .MuiInputBase-input': { py: '6px' }
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                  
                  {/* NONE Column */}
                  <Box sx={{ minWidth: 180, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#333', fontWeight: 'bold', fontSize: '0.85rem' }}>Select Parent Value</Typography>
                      <Select 
                        fullWidth 
                        size="small" 
                        value="NONE" 
                        sx={{ 
                          mt: 0.5,
                          bgcolor: '#f2f2f2', 
                          backgroundImage: 'linear-gradient(to bottom, #ffffff, #e6e6e6)',
                          borderRadius: '4px',
                          '& .MuiSelect-select': { py: '6px' }
                        }}
                      >
                        <MenuItem value="NONE">NONE</MenuItem>
                      </Select>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#333', fontWeight: 'bold', fontSize: '0.85rem' }}>Select Child Values</Typography>
                      <Select 
                        multiple 
                        fullWidth 
                        size="small" 
                        value={[]} 
                        displayEmpty 
                        sx={{ 
                          mt: 0.5,
                          bgcolor: '#f2f2f2', 
                          backgroundImage: 'linear-gradient(to bottom, #ffffff, #e6e6e6)',
                          borderRadius: '4px',
                          '& .MuiSelect-select': { py: '6px' }
                        }}
                      >
                        <MenuItem value="" disabled>Nothing selected</MenuItem>
                      </Select>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#333', fontWeight: 'bold', fontSize: '0.85rem' }}>Select Default Values</Typography>
                      <TextField fullWidth size="small" placeholder="Select default values" sx={{ mt: 0.5, '& .MuiOutlinedInput-root': { borderRadius: '4px', bgcolor: '#fff' }, '& .MuiInputBase-input': { py: '6px' } }} />
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button 
                    variant="contained" 
                    color="success" 
                    onClick={() => {
                      const childField = depChildFields.find(f => String(f.Id) === String(selectedDepChildId));
                      // Mock adding dependency info to the field object for UI preview
                      const updatedFields = fieldsList.map(f => {
                        if (String(f.field_ID) === String(fieldId) || f.fieldname === fieldName) {
                          const newF = { ...f };
                          if (activeDependencyMode === 'Parent') {
                            newF.Parent = childField?.Text || selectedDepChildId;
                          } else {
                            newF.Child = childField?.Text || selectedDepChildId;
                          }
                          return newF;
                        }
                        return f;
                      });
                      setFieldsList(updatedFields);

                      if (activeDependencyMode === 'Parent') {
                        setParentFieldId(selectedDepChildId);
                      } else {
                        setChildFieldId(selectedDepChildId);
                      }

                      setAddedMappings([...addedMappings, {
                        id: Date.now(),
                        text: childField ? childField.Text : selectedDepChildId,
                        type: activeDependencyMode
                      }]);
                      setIsDepModalOpen(false);
                    }}
                    sx={{ 
                      textTransform: 'none', 
                      borderRadius: '4px', 
                      px: 4, 
                      bgcolor: '#28a745', 
                      '&:hover': { bgcolor: '#218838' },
                      fontWeight: 'bold'
                    }}
                  >
                    Finish
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #eee', bgcolor: '#f9f9f9', p: 1.5 }}>
          <Button onClick={() => setIsDepModalOpen(false)} sx={{ textTransform: 'none', color: '#1976d2' }}>Cancel</Button>
        </DialogActions>
      </Dialog>
      {alertConfig.show && (
        <Alert
          show={alertConfig.show}
          type={alertConfig.type}
          title={alertConfig.title}
          message={alertConfig.message}
          onConfirm={alertConfig.onConfirm}
          onClose={() => setAlertConfig(prev => ({ ...prev, show: false }))}
        />
      )}
    </Box>
  );
};

export default EnquiryCustomField;
